import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import useSessionPersistedState from '../../../hooks/useSessionPersistedState';

export const ALL_DOCUMENT_MODES = 'ALL';

const DEFAULT_DATE_RANGE = { document_date: { type: 'date', start: null, end: null } };
const DEFAULT_PAGINATION = { page: 0, size: 10 };

const useSalesInvoiceRegisterListFilters = ({ defaultQuery = {}, searchOnChange = false, persistKey } = {}) => {
  const [selectedFilter, setSelectedFilter] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedFilter` : undefined,
    'document_number',
  );
  const [searchTerm, setSearchTerm] = useSessionPersistedState(persistKey ? `${persistKey}:searchTerm` : undefined, '');
  const [documentModeFilter, setDocumentModeFilter] = useSessionPersistedState(
    persistKey ? `${persistKey}:documentModeFilter` : undefined,
    ALL_DOCUMENT_MODES,
  );
  const [dateRange, setDateRange] = useSessionPersistedState(
    persistKey ? `${persistKey}:dateRange` : undefined,
    DEFAULT_DATE_RANGE,
  );
  const [pagination, setPagination] = useSessionPersistedState(
    persistKey ? `${persistKey}:pagination` : undefined,
    DEFAULT_PAGINATION,
  );

  const skipFilterPageReset = useRef(true);
  const skipSearchPageReset = useRef(true);

  const buildQuery = () => {
    const query = { ...defaultQuery, ...pagination };

    if (searchTerm) {
      query[selectedFilter] = searchTerm;
    }
    if (documentModeFilter && documentModeFilter !== ALL_DOCUMENT_MODES) {
      query.document_mode = documentModeFilter;
    }
    if (dateRange?.document_date?.start || dateRange?.document_date?.end) {
      const { start, end } = dateRange.document_date;
      const cleanDateObject = { ...(start && { start }), ...(end && { end }) };
      if (Object.keys(cleanDateObject).length) {
        query.document_date = JSON.stringify(cleanDateObject);
      }
    }

    return query;
  };

  const [currentQuery, setCurrentQuery] = useState(() => buildQuery());

  useEffect(() => {
    if (skipFilterPageReset.current) {
      skipFilterPageReset.current = false;
      return;
    }
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [selectedFilter, documentModeFilter, dateRange, setPagination]);

  useEffect(() => {
    setCurrentQuery(buildQuery());
  }, [selectedFilter, documentModeFilter, dateRange, pagination]);

  useEffect(() => {
    if (!searchOnChange) return;

    const handler = debounce(() => {
      setCurrentQuery(buildQuery());
      if (skipSearchPageReset.current) {
        skipSearchPageReset.current = false;
        return;
      }
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 500);

    handler();
    return () => handler.cancel();
  }, [searchTerm]);

  const handleFilterChange = (event) => setSelectedFilter(event.target.value);

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const handleDocumentModeFilterChange = (event) => setDocumentModeFilter(event.target.value);

  const handleDateRangeChange = (newDateRange) => setDateRange(newDateRange);

  const handlePaginationChange = (page, size) => setPagination({ page, size });

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      setCurrentQuery(buildQuery());
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  return {
    selectedFilter,
    searchTerm,
    currentQuery,
    dateRange,
    documentModeFilter,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handleDateRangeChange,
    handleDocumentModeFilterChange,
    handlePaginationChange,
  };
};

export default useSalesInvoiceRegisterListFilters;
